<div *ngIf="isVisible()" class="popup-overlay" (click)="onOverlayClick($event)">
    <div class="popup-modal">
        <!-- Close Button -->
        <button class="close-button" (click)="onClose()" aria-label="Close popup">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
        </button>

        <!-- Header -->
        <div class="popup-header">
            <h2 class="popup-title">{{ t('popup.welcome.title') }}</h2>
            <p class="popup-subtitle">{{ t('popup.welcome.subtitle') }}</p>
        </div>

        <!-- Error Message -->
        @if (errorMessage()) {
        <div class="error-message">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>{{ errorMessage() }}</span>
        </div>
        }

        <!-- Loading State -->
        @if (isLoadingPlans()) {
        <div class="loading-container">
            <div class="spinner"></div>
            <p>{{ t('popup.loading') }}</p>
        </div>
        }

        <!-- Plans Container -->
        @if (!isLoadingPlans() && (basicPlan() || premiumPlan())) {
        <!-- Billing Toggle -->
        <div class="billing-toggle-container">
            <button class="billing-option" [class.active]="!isYearlyBilling()" (click)="isYearlyBilling.set(false)">
                {{ t('popup.billing.monthly') }}
            </button>

            <div class="toggle-switch" (click)="toggleBillingPeriod()">
                <div class="toggle-slider" [class.yearly]="isYearlyBilling()"></div>
            </div>

            <button class="billing-option" [class.active]="isYearlyBilling()" (click)="isYearlyBilling.set(true)">
                {{ t('popup.billing.yearly') }}
            </button>

            @if (basicPlan() && basicPlan()!.yearlyDiscountRate > 0) {
            <span class="discount-badge">
                {{ t('popup.billing.saveUpTo') }} {{ basicPlan()!.yearlyDiscountRate }}%
            </span>
            }
        </div>

        <div class="plans-container">
            <!-- Basic Plan -->
            @if (basicPlan()) {
            <div class="plan-card">
                <div class="plan-header">
                    <h3 class="plan-name">{{ basicPlan()!.label || basicPlan()!.name }}</h3>
                    <p class="plan-description">{{ t('popup.plan.basic.description') }}</p>
                </div>

                <!-- Price -->
                <div class="plan-price">
                    <span class="price-amount">{{ formatPrice(basicPlan()!) }}</span>
                    <span class="price-currency">F CFA</span>
                    <span class="price-period">
                        {{ isYearlyBilling() ? t('popup.billing.perMonthYearly') : t('popup.billing.perMonth') }}
                    </span>
                </div>

                @if (isYearlyBilling() && basicPlan()!.yearlyDiscountRate > 0) {
                <div class="savings-badge">
                    {{ t('popup.billing.saveUpTo') }} {{ basicPlan()!.yearlyDiscountRate }}%
                </div>
                }

                <div class="plan-features">
                    <div class="feature-item">
                        <svg class="feature-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        <span>{{ t('popup.plan.basic.feature1') }}</span>
                    </div>
                    <div class="feature-item">
                        <svg class="feature-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        <span>{{ t('popup.plan.basic.feature2') }}</span>
                    </div>
                    <div class="feature-item">
                        <svg class="feature-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        <span>{{ t('popup.plan.basic.feature3') }}</span>
                    </div>
                </div>

                <button class="plan-button plan-button-basic" (click)="onSelectPlan('basic')"
                    [disabled]="isProcessingBasic()">
                    @if (isProcessingBasic()) {
                    <span class="button-spinner"></span>
                    } @else {
                    {{ t('popup.plan.choosePlan') }}
                    }
                </button>
            </div>
            }

            <!-- Premium Plan -->
            @if (premiumPlan()) {
            <div class="plan-card plan-card-premium">
                <div class="popular-badge">{{ t('popup.plan.mostPopular') }}</div>

                <div class="plan-header">
                    <h3 class="plan-name">{{ premiumPlan()!.label || premiumPlan()!.name }}</h3>
                    <p class="plan-description">{{ t('popup.plan.premium.description') }}</p>
                </div>

                <!-- Price -->
                <div class="plan-price">
                    <span class="price-amount">{{ formatPrice(premiumPlan()!) }}</span>
                    <span class="price-currency">F CFA</span>
                    <span class="price-period">
                        {{ isYearlyBilling() ? t('popup.billing.perMonthYearly') : t('popup.billing.perMonth') }}
                    </span>
                </div>

                @if (isYearlyBilling() && premiumPlan()!.yearlyDiscountRate > 0) {
                <div class="savings-badge savings-badge-premium">
                    {{ t('popup.billing.saveUpTo') }} {{ premiumPlan()!.yearlyDiscountRate }}%
                </div>
                }

                <div class="plan-features">
                    <div class="feature-item">
                        <svg class="feature-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        <span>{{ t('popup.plan.premium.feature1') }}</span>
                    </div>
                    <div class="feature-item">
                        <svg class="feature-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        <span>{{ t('popup.plan.premium.feature2') }}</span>
                    </div>
                    <div class="feature-item">
                        <svg class="feature-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        <span>{{ t('popup.plan.premium.feature3') }}</span>
                    </div>
                    <div class="feature-item">
                        <svg class="feature-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        <span>{{ t('popup.plan.premium.feature4') }}</span>
                    </div>
                </div>

                <button class="plan-button plan-button-premium" (click)="onSelectPlan('premium')"
                    [disabled]="isProcessingPremium()">
                    @if (isProcessingPremium()) {
                    <span class="button-spinner"></span>
                    } @else {
                    {{ t('popup.plan.choosePlan') }}
                    }
                </button>
            </div>
            }
        </div>
        }
    </div>
</div>